import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useListState, Selection } from 'react-stately'
import { isEmpty } from 'remeda'
import { ListProps } from 'react-stately'
import { DynamicCascadeItem, DynamicItem, dynamicItemToItem } from '../base-select/dynamicItemUtils'
import { useCascadeListFocus } from '../base-select/CascadeListFocusContext'
import { useCascadeSelectState } from '../base-select/useCascaseSelectState'
import { createCascadePath } from '../base-select/createCascadePath'
import { useSelectListData } from './useSelectListData'
import { useSelectListKeyboardDelegate } from './useSelectListKeyboardDelegate'

type Options = {
  children: React.ReactNode
  search?: string
  onSelectionChange: (selection: Selection) => void
  onSelectCascadeItem: (item: DynamicCascadeItem) => void
} & (
  | {
      variant: 'single'
      value?: string
      valuePath?: readonly string[]
      onChange?: (val?: string, path?: readonly string[]) => void
    }
  | {
      variant: 'multi'
      value?: readonly string[]
      valuePath?: never
      onChange?: (value?: string[]) => void
    }
)

export const useSelectListState = ({
  children,
  search,
  variant,
  value,
  valuePath,
  onSelectionChange,
  onSelectCascadeItem,
  onChange,
}: Options) => {
  const isDirty = useRef(false)
  const { ref: setListRef, getCollection, queueFocus, observeCollection } = useCascadeListFocus()
  const [selectedCascadeItem, setSelectedCascadeItem] = useState<DynamicCascadeItem[] | undefined>()
  const listData = useSelectListData({
    children,
    componentName: 'SelectList',
    search: isEmpty(selectedCascadeItem ?? []) ? search : undefined,
  })

  const setSelectedCascadeItemHandler = useCallback(
    (
      items?:
        | ((items?: DynamicCascadeItem[]) => DynamicCascadeItem[] | undefined)
        | DynamicCascadeItem[]
    ) => {
      isDirty.current = true
      if (typeof items === 'function') {
        setSelectedCascadeItem(items(selectedCascadeItem))
      } else {
        setSelectedCascadeItem(items)
      }
    },
    [selectedCascadeItem, setSelectedCascadeItem]
  )

  const selectedKeys = useMemo(() => {
    if (variant === 'multi') {
      return value?.filter(v => listData.existingValues.has(v)) ?? []
    } else {
      if (selectedCascadeItem?.length) {
        return selectedCascadeItem.map(item => item.value).slice(0, 1)
      }
      if (listData.existingValues.has(value as string)) {
        return [value as string]
      }
      if (valuePath?.length) {
        return valuePath.slice(0, 1)
      }
      return []
    }
  }, [variant, value, listData.existingValues, selectedCascadeItem, valuePath])

  const onSelectionChangeHandler = useCallback(
    (selection: Selection) => {
      setSelectedCascadeItemHandler([])
      onSelectionChange(selection)
    },
    [setSelectedCascadeItemHandler, onSelectionChange]
  )

  const ariaProps: ListProps<DynamicItem> = {
    disabledKeys: listData.disabledKeys,
    children: dynamicItemToItem,
    items: listData.options,
    selectedKeys,
    selectionMode: variant === 'multi' ? 'multiple' : 'single',
    onSelectionChange: onSelectionChangeHandler,
  }

  const onSelectCascadeItemHandler = useCallback(
    (item: DynamicCascadeItem) => {
      queueFocus(item.value)
      setSelectedCascadeItemHandler([item])
      onSelectCascadeItem(item)
    },
    [queueFocus, setSelectedCascadeItemHandler, onSelectCascadeItem]
  )

  const state = useCascadeSelectState(useListState(ariaProps), onSelectCascadeItemHandler)
  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)

  const onChangeHandler = useCallback(
    (val: string, path: string[]) => {
      if (variant === 'single') {
        onChange?.(val, path)
        setSelectedCascadeItemHandler(
          selectedCascadeItem =>
            selectedCascadeItem?.filter(item => path?.includes(item.value)) ?? []
        )
      }
    },
    [variant, onChange, setSelectedCascadeItemHandler]
  )

  useEffect(
    () =>
      setListRef(
        {
          keyboardDelegate,
          selectionManager: state.selectionManager,
          collection: state.collection,
        },
        ''
      ),
    [keyboardDelegate, state.selectionManager, state.collection, setListRef]
  )

  useEffect(() => {
    const recheckSelectedCascadeItems = () => {
      if (valuePath?.length) {
        const rootItem = state.collection?.getItem(valuePath[0])?.value

        if (rootItem?.itemType === 'cascade' && valuePath) {
          const selectedCascadeItems = createCascadePath(valuePath, rootItem, getCollection)

          setSelectedCascadeItem?.(selectedCascadeItems)
          if (selectedCascadeItems.length < valuePath.length) {
            observeCollection(
              valuePath.slice(0, selectedCascadeItems.length).join('.'),
              recheckSelectedCascadeItems
            )
          }
        }
      }
    }

    if (!isDirty.current) {
      recheckSelectedCascadeItems()
    }
  }, [valuePath, getCollection, state.collection, setSelectedCascadeItem, observeCollection])

  return {
    listData,
    state,
    selectedKeys,
    keyboardDelegate,
    selectedCascadeItem,
    setSelectedCascadeItem: setSelectedCascadeItemHandler,
    onChange: onChangeHandler,
  }
}
